import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Table, Spinner, Badge, Row, Card, Container, Button } from "react-bootstrap";
import axios from "axios";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import Swal from "sweetalert2";
import { toWords } from 'number-to-words';
import { FaArrowLeft, FaPrint } from 'react-icons/fa';

const PumpDomesticQuotationView = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [quotationData, setQuotationData] = useState(null);
    const [rows, setRows] = useState([]);

    const fetchQuotationData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}quotation/findById/${id}`, {
                headers: authHeader()
            });

            const data = response.data;
            setQuotationData(data);

            if (data.quotationItems && data.quotationItems.length > 0) {
                setRows(data.quotationItems.map(item => ({
                    description: item.product?.description || '',
                    productremarks: item.productremarks || "",
                    quantity: item.quantity,
                    unitrate: item.unitrate,
                    amount: item.amount,
                    hsnsacno: item.hsnsacno,
                    partno: item.partno,
                    serialnumber: item.serialnumber,
                    modelnumber: item.modelnumber,
                    tagnumber: item.tagnumber,
                    moc: item.moc || item.product?.moc || "",
                    itemcode: item.itemcode || "",
                    uom: item.uom,
                    gst: item.gst,
                    totalinr: item.totalinr
                })));
            }
        } catch (error) {
            console.error("Error fetching quotation data", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to fetch quotation data. Please try again.",
            });
            navigate("/quotationIndexTable/1");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchQuotationData();
    }, [id]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
        }).format(value || 0);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Draft': return 'secondary';
            case 'Expired': return 'warning';
            case 'Cancelled': return 'danger';
            default: return 'info';
        }
    };

    const getAddressString = (address) => {
        if (!address) return '-';
        return [
            address.customeraddress,
            address.city?.cityname,
            address.state?.statename,
            address.pincode,
            address.country?.countryname
        ].filter(Boolean).join(', ');
    };

    const numberToWordsWithRupees = (num) => {
        if (isNaN(num)) return "Zero Rupees Only";
        const amount = Math.round(num);
        const words = toWords(amount);
        const capitalizedWords = words.split(' ').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
        return `${capitalizedWords} Rupees Only`;
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!quotationData) {
        return <div className="alert alert-danger text-center mt-4">Quotation not found</div>;
    }

    // Calculate totals based on your existing logic
    const nettotal = rows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
    const pAndF = nettotal * (quotationData.packingandforwardingpercent / 100);
    const freight = nettotal * (quotationData.freightpercent / 100);
    const subtotal = nettotal + pAndF + freight;
    const gst = subtotal * (quotationData.gstpercent / 100);
    const grandtotalinr = subtotal + gst;
    const roundedGrandTotal = Math.round(grandtotalinr);

    return (
        <Container fluid className="p-3">
            {/* Header Section */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold text-primary mb-0">SALES QUOTATION - VIEW</h5>
                        <div>
                            <Button variant="outline-white" size="sm" className="me-2"
                                onClick={() => navigate(`/CRM/SOQuotation/PumpDomesticQuotation/PumpAraiDomesticSalesQuotationPrint/${id}`)}
                            >
                                <FaPrint className="me-1" />
                                PRINT
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
                                <FaArrowLeft className="me-1" />
                                Back
                            </Button>
                        </div>
                    </div>

                    <Row className="g-2">
                        <Col md={3}>
                            <div className="border-start border-3 border-primary ps-2">
                                <small className="text-muted">Quotation No</small>
                                <div className="fw-bold">{quotationData.quotationno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-success ps-2">
                                <small className="text-muted">Quotation Date</small>
                                <div className="fw-bold">{formatDate(quotationData.quotationdate)}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-warning ps-2">
                                <small className="text-muted">Enquiry Ref No</small>
                                <div className="fw-bold">{quotationData.enquiryrefno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-info ps-2">
                                <small className="text-muted">Status</small>
                                <div>
                                    <Badge
                                        bg={getStatusBadge(quotationData.status)}
                                        className="px-2 py-1"
                                    >
                                        {quotationData.status || '-'}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Quotation Details */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Quotation Details</h6>
                </Card.Header>
                <Card.Body className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Customer Enquiry No</small>
                                <div className="fw-bold">{quotationData.customerenquirynumber || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Enquiry Ref Date</small>
                                <div className="fw-bold">{formatDate(quotationData.enquiryrefdate)}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Customer Material Code</small>
                                <div className="fw-bold">{quotationData.customermaterialcode || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Quote Validity</small>
                                <div className="fw-bold">{quotationData.quotevalidity || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Delivery Term</small>
                                <div className="fw-bold">{quotationData.deliveryterm || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Payment Term</small>
                                <div className="fw-bold">{quotationData.paymentterm || '-'}</div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Customer Information */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Customer Information</h6>
                </Card.Header>
                <Card.Body className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Company Name</small>
                                <div className="fw-bold">{quotationData.customer?.companyname || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">GST No</small>
                                <div className="fw-bold">{quotationData.customer?.gstno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Contact Person</small>
                                <div className="fw-bold">{quotationData.contactperson || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Phone</small>
                                <div className="fw-bold">{quotationData.contactno || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Email</small>
                                <div className="fw-bold">{quotationData.email || '-'}</div>
                            </div>
                        </Col>
                        <Col md={12}>
                            <div className="mt-2">
                                <small className="text-muted">Address</small>
                                <div className="fw-bold">{getAddressString(quotationData.customeraddress)}</div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Quotation Items */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Quotation Items</h6>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table bordered size="sm" className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center fw-bold py-2">#</th>
                                    <th className="fw-bold py-2">Description</th>
                                    <th className="fw-bold py-2">Part No</th>
                                    <th className="fw-bold py-2">HSN Code</th>
                                    <th className="fw-bold py-2">Serial No</th>
                                    <th className="fw-bold py-2">Model No</th>
                                    <th className="fw-bold py-2">Tag No</th>
                                    <th className="fw-bold py-2">MOC</th>
                                    <th className="fw-bold py-2">Item Code</th>
                                    <th className="fw-bold py-2 text-center">Qty</th>
                                    <th className="fw-bold py-2 text-center">UOM</th>
                                    <th className="fw-bold py-2 text-end">Unit Price</th>
                                    <th className="fw-bold py-2 text-end">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, index) => (
                                    <tr key={index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>
                                            <div className="fw-semibold">{row.description || '-'}</div>
                                            {row.productremarks && (
                                                <div className="text-muted small mt-1">
                                                    <small><strong>Remarks:</strong> {row.productremarks}</small>
                                                </div>
                                            )}
                                        </td>
                                        <td>{row.partno || '-'}</td>
                                        <td>{row.hsnsacno || '-'}</td>
                                        <td>{row.serialnumber || '-'}</td>
                                        <td>{row.modelnumber || '-'}</td>
                                        <td>{row.tagnumber || '-'}</td>
                                        <td>{row.moc || '-'}</td>
                                        <td>{row.itemcode || '-'}</td>
                                        <td className="text-center">{row.quantity || '0'}</td>
                                        <td className="text-center">{row.uom || '-'}</td>
                                        <td className="text-end">{formatCurrency(row.unitrate)}</td>
                                        <td className="text-end">{formatCurrency(row.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <td colSpan="12" className="text-end fw-bold py-2">
                                        Net Total:
                                    </td>
                                    <td className="text-end fw-bold py-2">
                                        {formatCurrency(nettotal)}
                                    </td>
                                </tr>
                            </tfoot>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {/* Calculation Summary */}
            <Row>
                <Col lg={6} className="ms-auto">
                    <Card className="shadow-sm border-0 rounded">
                        <Card.Header className="bg-light py-2">
                            <h6 className="mb-0 fw-bold text-center">Amount Calculation</h6>
                        </Card.Header>
                        <Card.Body className="p-3">
                            <div className="space-y-2">
                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">Net Total:</span>
                                    <span className="fw-bold">{formatCurrency(nettotal)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">
                                        Packing & Forwarding ({quotationData.packingandforwardingpercent || '0'}%):
                                    </span>
                                    <span className="fw-bold">{formatCurrency(pAndF)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">
                                        Freight ({quotationData.freightpercent || '0'}%):
                                    </span>
                                    <span className="fw-bold">{formatCurrency(freight)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">Subtotal:</span>
                                    <span className="fw-bold">{formatCurrency(subtotal)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">
                                        GST ({quotationData.gstpercent || '18'}%):
                                    </span>
                                    <span className="fw-bold">{formatCurrency(gst)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-2 border-top">
                                    <span className="fw-bold">Grand Total</span>
                                    <span className="fw-bold text-primary">{formatCurrency(grandtotalinr)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-2">
                                    <span className="fw-bold">Rounded Total</span>
                                    <span className="fw-bold text-success">{formatCurrency(roundedGrandTotal)}</span>
                                </div>

                                <div className="mt-3 pt-2 border-top">
                                    <div className="text-center">
                                        <small className="text-muted fw-bold">Amount in Words</small>
                                        <div className="fw-semibold text-dark small">
                                            {numberToWordsWithRupees(roundedGrandTotal)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Footer Actions */}
            <div className="text-center mt-3 pt-2 border-top">
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate(-1)}
                >
                    <FaArrowLeft className="me-1" />
                    Back to List
                </Button>
            </div>
        </Container>
    );
};

export default PumpDomesticQuotationView;